import cn from "classnames";
import { Fragment, type ReactNode, useState } from "react";
import { Button, DialogTrigger, Popover } from "react-aria-components";
import type { Size } from "./button";
import { buttonShapeStyle } from "./button";
import { IconChevronDown } from "./icons";
import { tokens } from "./tokens";

export interface DropdownMenuItemProps {
  id: string;
  label: ReactNode;
  /** Receives a `close` callback — call it to dismiss the menu. */
  onClick?: (close: () => void) => void;
  /** Renders a divider above this item */
  hasSeparator?: boolean;
  className?: string;
  disabled?: boolean;
}

export interface DropdownMenuProps {
  /** The label rendered inside the default trigger button */
  label: ReactNode;
  items: DropdownMenuItemProps[];
  variant?: "primary" | "secondary" | "white" | "delete" | "success";
  size?: Size;
  disabled?: boolean;
  placement?:
    | "bottom"
    | "bottom start"
    | "bottom end"
    | "top"
    | "top start"
    | "top end";
  className?: string;
}

export const DropdownMenu = ({
  label,
  items,
  variant = "white",
  size = "sm",
  disabled = false,
  placement = "bottom end",
  className = "",
}: DropdownMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const close = () => setIsOpen(false);

  const triggerClasses = cn(
    "flex items-center gap-1.5",
    tokens.buttons.sizes[size],
    tokens.buttons.styles[variant],
    buttonShapeStyle(size, "button"),
    { "opacity-50 cursor-not-allowed": disabled },
    className,
  );

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <Button className={triggerClasses} isDisabled={disabled}>
        {label}
        <IconChevronDown variant="sm" />
      </Button>
      <Popover placement={placement} offset={4}>
        <div className="bg-white border border-black-100 rounded-md shadow-lg py-1 min-w-[160px]">
          {items.map((item) => (
            <Fragment key={item.id}>
              {item.hasSeparator && (
                <div className="my-1 border-t border-black-100" />
              )}
              <button
                type="button"
                className={cn(
                  "flex items-center justify-between gap-4 w-full px-3 py-2 text-gray-900 cursor-pointer select-none hover:bg-gray-50 focus:bg-gray-50 outline-none",
                  item.className,
                  {
                    "opacity-50 cursor-not-allowed pointer-events-none":
                      item.disabled,
                  },
                )}
                disabled={item.disabled}
                onClick={() => item.onClick?.(close)}
              >
                {item.label}
              </button>
            </Fragment>
          ))}
        </div>
      </Popover>
    </DialogTrigger>
  );
};
